import { Injectable } from '@angular/core';
import {
    GlobalSearchResult,
    GlobalSearchService,
    PersonSearchField
} from '@predictiveindex/pi-client-global-search-lib';
import {
    from,
    Observable,
    shareReplay,
    catchError,
    throwError,
    switchMap,
    of,
    map,
    tap
} from 'rxjs';
import { Person } from '../core-data-model/Person.model';
import { Util } from '../core-utilities';
import { InitDataService } from '../system-services/InitData/InitData.service';
import { AssessmentsApi } from './AssessmentsApi.connect';
import { PersonApi } from './PersonApi.connect';
import { SelfIdentityMatchState } from './SelfIdentity.dataservice';

/**
 * For matching the User to the requisite SurveyUser (Person).
 * For clarity, SurveyUser refers to the record in the SurveyUser table,
 * and Person refers to a superset of SurveyUser that contains other details.
 * Replaces {@link SelfIdentityDataService} for PI2 resolution of User - SurveyUser resolution
 */
@Injectable({ providedIn: 'root' })
export class MatchIdentityDataService {
    self: Person;
    matchState: SelfIdentityMatchState;
    private matchedPerson$: Observable<Person>;

    constructor(
        private globalSearch: GlobalSearchService,
        private personApi: PersonApi,
        private assessmentsApi: AssessmentsApi,
        private initData: InitDataService,
        private util: Util
    ) {
        this.matchState = this.initData.user.surveyUserId
            ? SelfIdentityMatchState.MatchExists
            : SelfIdentityMatchState.MatchNotFound;
    }

    /**
     * Public method to resolve the User - SurveyUser association and returning the SurveyUser as Person.
     * Ensures the attempt to match the User and SurveyUser is only done once. Once the Survey User is
     * retrieved, it is converted to an instance of Person.
     */
    get matchedPersonForUser$(): Observable<Person> {
        if (this.matchedPerson$) {
            return this.matchedPerson$;
        }
        // We do not wish to execute the calls again for multiple subscribers,
        // so new subscribers will only get the last emitted value
        this.matchedPerson$ = from(this.resolveMatchState()).pipe(
            shareReplay(1),
            catchError((error) => {
                console.error(
                    'Something wrong happened while matching the current User to an existing SurveyUser',
                    error
                );
                this.matchedPerson$ = null;
                return throwError(() => error);
            })
        );

        return this.matchedPerson$;
    }

    /**
     * Given the Person, set its associated SurveyUser to the current User,
     * based on the current user context. Persists to database.
     * @param person a Person is provided but it is SurveyUser that is being updated
     */
    associatePersonToUser(person: Person): Observable<void> {
        if (this.self && this.self.id !== person.id) {
            return this.clearPersonUserAssociation().pipe(
                tap(() => {
                    this.setPersonAssociation(person);
                }),
                map(() => null) // Return Observable<void>
            );
        }
        this.setPersonAssociation(person);
        return of(null);
    }

    /**
     * For any SurveyUser that has been associated with the current User,
     * remove its association with that User. Persists to database.
     */
    clearPersonUserAssociation(): Observable<void> {
        if (!this.self) {
            return of(null);
        }
        return from(this.personApi.unsetAssociation(this.self.id)).pipe(
            map(() => {
                this.initData.user.surveyUserId = null;
                this.self = null;
                this.matchState = SelfIdentityMatchState.MatchNotFound;
            })
        );
    }

    /**
     * This method determines which search result, if any, should be used to
     * associate with the user. This logic is changed from {@link SelfIdentityDataService}
     */
    getPotentialMatch(): Observable<Person> {
        return from(
            this.globalSearch.searchPeople(
                this.initData.user.companyId,
                this.initData.user.email,
                PersonSearchField.BY_EMAIL
            )
        ).pipe(
            map((results: GlobalSearchResult[]) => {
                const peopleWithMatchingEmails = results.filter(
                    (r) =>
                        r.email &&
                        r.email.toLowerCase() ===
                            this.initData.user.email.toLowerCase()
                );
                peopleWithMatchingEmails.sort(
                    this.globalSearchSortFn.bind(this)
                );

                if (peopleWithMatchingEmails.length > 0) {
                    const bestMatch = peopleWithMatchingEmails[0];
                    return bestMatch;
                }
                return null;
            }),
            switchMap((bestMatch: GlobalSearchResult | null) =>
                bestMatch ? this.getPerson(bestMatch.id) : of(null)
            )
        );
    }

    /**
     * If a SurveyUser is attached to the current User (from InitData), get their person details.
     * If not, search for a SurveyUser match for the current User (using GlobalSearch).
     * If no match is found, return null.
     */
    private resolveMatchState(): Observable<Person> {
        if (this.matchState === SelfIdentityMatchState.MatchExists) {
            return this.getPerson().pipe(
                map((me) => {
                    this.self = me;
                    return me;
                })
            );
        } else {
            return this.getPotentialMatch().pipe(
                switchMap((match) => {
                    this.matchState = match
                        ? SelfIdentityMatchState.MatchFound
                        : SelfIdentityMatchState.MatchNotFound;

                    if (match) {
                        return this.associatePersonToUser(match).pipe(
                            map(() => match) // Return the original match after the side-effect completes
                        );
                    }

                    return of(null);
                })
            );
        }
    }

    private getPerson(personId?: string): Observable<Person> {
        personId = personId ?? this.initData.user.surveyUserId;
        return from(
            this.assessmentsApi.getBehavioralAssessmentV2(
                personId,
                this.initData.user.companyId
            )
        ).pipe(
            map((baPerson: Person) => {
                this.self = baPerson;
                return baPerson;
            })
        );
    }

    private globalSearchSortFn(
        resultA: GlobalSearchResult,
        resultB: GlobalSearchResult
    ): number {
        const dateA = this.util
            .newDate(resultA.lastAssessmentCompletedDate)
            .getTime();
        const dateB = this.util
            .newDate(resultB.lastAssessmentCompletedDate)
            .getTime();
        if (
            !resultA.lastAssessmentCompletedDate &&
            !resultB.lastAssessmentCompletedDate
        ) {
            return 0;
        } else if (!resultB.lastAssessmentCompletedDate) {
            return -1;
        } else if (!resultA.lastAssessmentCompletedDate) {
            return 1;
        } else {
            return dateA - dateB;
        }
    }

    clearMatchedPerson(): void {
        this.matchedPerson$ = null;
        this.self = null;
    }

    private setPersonAssociation(person: Person): void {
        this.personApi.setAssociation(person.id, this.initData.user.userGuid);
        this.initData.user.surveyUserId = person.id;
        this.self = person;
        this.matchState = SelfIdentityMatchState.MatchExists;
    }
}
